// src/pages/Settings.js
import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Avatar,
  Chip,
  Button,
  Stack,
  TextField,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AccountBalanceWalletOutlined from "@mui/icons-material/AccountBalanceWalletOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import {
  useGetProfileQuery,
  useNafathDocumentsMutation,
  useUpdateVaraVerificationDocumentMutation,
  useConnectStripeAccountMutation,
  useGetStripeStatusQuery,
} from "../../../redux/api/authApi";
import SettingsSkeleton from "./SettingsSkeleton";
import { authStorageGet } from "../../../utils/authStorage";
import InvestorConnectWalletDialog, {
  useDisconnectLinkedWallet,
} from "../../investor/InvestorConnectWalletDialog";
import { OWNER_WALLET_STORAGE_KEYS } from "../ownerWalletStorageKeys";

const WALLET_CHAIN_ID = Number(process.env.REACT_APP_WALLET_CHAIN_ID) || 1;

function blockExplorerAddressUrl(address) {
  const a = String(address || "");
  if (!a) return "#";
  if (WALLET_CHAIN_ID === 137) return `https://polygonscan.com/address/${a}`;
  if (WALLET_CHAIN_ID === 56) return `https://bscscan.com/address/${a}`;
  return `https://etherscan.io/address/${a}`;
}

function networkLabel(chainId) {
  if (chainId === 137) return "Polygon Network";
  if (chainId === 56) return "BNB Chain";
  if (chainId === 1) return "Ethereum Mainnet";
  return `Chain ID: ${chainId}`;
}

function formatProfileDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

function normalizeNafathFileKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

function nafathFileLabel(value) {
  const key = normalizeNafathFileKey(value);
  if (key === "idproofdocument" || key === "idproof") return "ID Proof Document";
  if (key === "addressproofdocument" || key === "addressproof")
    return "Address Proof Document";
  return String(value || "Document");
}

function normalizeVaraFileKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

function varaFileLabel(value) {
  const key = normalizeVaraFileKey(value);
  if (key === "passportdoc" || key === "passport") return "Passport";
  if (key === "proofoffundsdoc" || key === "proofoffunds") return "Proof of funds";
  if (key === "proofofaddressdoc" || key === "proofofaddress" || key === "addressproof")
    return "Proof of address";
  return String(value || "Document");
}

function Settings() {
  const navigate = useNavigate();
  const location = useLocation();
  const stripeReauthTriggeredRef = useRef(false);
  const {
    data: profileResponse,
    isLoading,
    isError,
    refetch: refetchProfile,
  } = useGetProfileQuery({});
  const [uploadNafathDocuments, { isLoading: isUploadingNafathDocuments }] =
    useNafathDocumentsMutation();
  const [updateVaraVerificationDocument, { isLoading: isUploadingVaraDocuments }] =
    useUpdateVaraVerificationDocumentMutation();
  const [connectStripeAccount, { isLoading: isConnectingStripe }] =
    useConnectStripeAccountMutation();
  const [stripeConnectError, setStripeConnectError] = useState("");
  const {
    data: stripeStatusResponse,
    isLoading: isLoadingStripeStatus,
    refetch: refetchStripeStatus,
  } = useGetStripeStatusQuery();
  const stripeStatus = stripeStatusResponse?.data;

  const handleConnectStripeAccount = async () => {
    setStripeConnectError("");
    // Open a placeholder tab synchronously to avoid popup blockers — browsers
    // block window.open() called after `await` because it loses user-gesture trust.
    const newTab = window.open("about:blank", "_blank");
    try {
      const res = await connectStripeAccount().unwrap();
      const onboardingUrl = res?.data?.onboardingUrl;
      if (!onboardingUrl) {
        throw new Error("Onboarding URL not received from server.");
      }
      if (newTab) {
        newTab.opener = null;
        newTab.location.href = onboardingUrl;
      } else {
        window.open(onboardingUrl, "_blank", "noopener,noreferrer");
      }
      refetchStripeStatus();
    } catch (err) {
      if (newTab) newTab.close();
      setStripeConnectError(
        err?.data?.message || err?.message || err?.error || "Could not start Stripe onboarding."
      );
    }
  };

  // Auto re-trigger Stripe onboarding when Stripe redirects back to /asset/stripe/reauth.
  // The popup-blocker workaround using window.open is not needed here because the
  // request itself is the trigger — we redirect the current tab to the new onboarding URL.
  useEffect(() => {
    if (location.pathname !== "/asset/stripe/reauth") return;
    if (stripeReauthTriggeredRef.current) return;
    stripeReauthTriggeredRef.current = true;
    (async () => {
      setStripeConnectError("");
      try {
        const res = await connectStripeAccount().unwrap();
        const onboardingUrl = res?.data?.onboardingUrl;
        if (!onboardingUrl) {
          throw new Error("Onboarding URL not received from server.");
        }
        window.location.replace(onboardingUrl);
      } catch (err) {
        setStripeConnectError(
          err?.data?.message || err?.message || err?.error || "Could not restart Stripe onboarding."
        );
        navigate("/asset/settings", { replace: true });
      }
    })();
  }, [location.pathname, connectStripeAccount, navigate]);

  const profile = profileResponse?.data;

  const [walletAddress, setWalletAddress] = useState(
    () => authStorageGet(OWNER_WALLET_STORAGE_KEYS.address) || ""
  );
  const [walletProvider, setWalletProvider] = useState(
    () => authStorageGet(OWNER_WALLET_STORAGE_KEYS.provider) || ""
  );
  const [connectOpen, setConnectOpen] = useState(false);
  const [isEditingNafathDocs, setIsEditingNafathDocs] = useState(false);
  const [idProofFile, setIdProofFile] = useState(null);
  const [addressProofFile, setAddressProofFile] = useState(null);
  const [nafathDocErrors, setNafathDocErrors] = useState({});
  const idProofInputRef = useRef(null);
  const addressProofInputRef = useRef(null);
  const passportDocInputRef = useRef(null);
  const proofOfFundsDocInputRef = useRef(null);
  const proofOfAddressDocInputRef = useRef(null);
  const disconnectLinked = useDisconnectLinkedWallet(OWNER_WALLET_STORAGE_KEYS);

  const [isEditingVaraDocs, setIsEditingVaraDocs] = useState(false);
  const [passportDocFile, setPassportDocFile] = useState(null);
  const [proofOfFundsDocFile, setProofOfFundsDocFile] = useState(null);
  const [proofOfAddressDocFile, setProofOfAddressDocFile] = useState(null);
  const [varaDocErrors, setVaraDocErrors] = useState({});
  const [varaPassportNumber, setVaraPassportNumber] = useState("");
  const [varaCryptoWalletAddress, setVaraCryptoWalletAddress] = useState("");
  const [varaSourceOfFunds, setVaraSourceOfFunds] = useState("");
  const [varaTradingExperience, setVaraTradingExperience] = useState("");

  const handleWalletLinked = useCallback(({ address, provider }) => {
    setWalletAddress(address || "");
    setWalletProvider(provider || "");
  }, []);

  const handleDisconnectWallet = useCallback(async () => {
    await disconnectLinked(walletAddress);
    setWalletAddress("");
    setWalletProvider("");
  }, [walletAddress, disconnectLinked]);

  // Seed wallet address from API profile when not already set via localStorage (same pattern as InvestorSettings)
  useEffect(() => {
    if (profile?.walletAddress && !authStorageGet(OWNER_WALLET_STORAGE_KEYS.address)) {
      setWalletAddress(profile.walletAddress);
    }
  }, [profile?.walletAddress]);

  const apiWalletVerified = !!profile?.walletVerified;
  const apiWalletVerifiedAt = profile?.walletVerifiedAt ?? null;

  const isKycVerified =
    profile?.kycStatus === "Approved" || profile?.kycStatus === "Verified";

  const fullName = profile
    ? `${profile.firstName || ""} ${profile.lastName || ""}`.trim()
    : "";

  const roleLabel =
    profile?.role === "assetOwner"
      ? "Asset Owner"
      : profile?.role
        ? profile.role
        : "User";

  const createdAtLabel = profile?.createdAt
    ? new Date(profile.createdAt).toLocaleDateString("en-GB", {
      month: "long",
      year: "numeric",
    })
    : "";

  const kycStatusText = profile?.kycStatus || "N/A";
  const nafathStatusText = profile?.nafathStatus || "N/A";
  const varaStatusText = profile?.verifiedByVARA || "N/A";
  const isVaraApproved =
    String(profile?.verifiedByVARA || "")
      .trim()
      .toLowerCase() === "approved";
  const nafathRejectionReason = String(profile?.nafathRejectionReason || "").trim();
  const nafathFilesNeedToBeUpdatedRaw = Array.isArray(profile?.nafathFilesNeedToBeUpdated)
    ? profile.nafathFilesNeedToBeUpdated
    : [];
  const normalizedNafathFilesToUpdate = nafathFilesNeedToBeUpdatedRaw.map(normalizeNafathFileKey);
  const shouldUpdateIdProof = normalizedNafathFilesToUpdate.some(
    (key) => key === "idproofdocument" || key === "idproof"
  );
  const shouldUpdateAddressProof = normalizedNafathFilesToUpdate.some(
    (key) => key === "addressproofdocument" || key === "addressproof"
  );
  const hasNafathDocUpdateRequest =
    shouldUpdateIdProof || shouldUpdateAddressProof;
  const showNafathRejectionDetails =
    Boolean(nafathRejectionReason) || normalizedNafathFilesToUpdate.length > 0;

  const varaDetails =
    profile?.varaDetails && typeof profile.varaDetails === "object"
      ? profile.varaDetails
      : null;
  const varaRejectionReason = String(varaDetails?.varaRejectionReason || "").trim();
  const varaFilesNeedToBeUpdatedRaw = Array.isArray(varaDetails?.varaFilesNeedToBeUpdated)
    ? varaDetails.varaFilesNeedToBeUpdated
    : [];
  const normalizedVaraFilesToUpdate = varaFilesNeedToBeUpdatedRaw.map(normalizeVaraFileKey);
  const shouldUpdatePassportDoc = normalizedVaraFilesToUpdate.some(
    (key) => key === "passportdoc" || key === "passport"
  );
  const shouldUpdateProofOfFundsDoc = normalizedVaraFilesToUpdate.some(
    (key) => key === "proofoffundsdoc" || key === "proofoffunds"
  );
  const shouldUpdateProofOfAddressDoc = normalizedVaraFilesToUpdate.some(
    (key) =>
      key === "proofofaddressdoc" ||
      key === "proofofaddress" ||
      key === "addressproofdocument" ||
      key === "addressproof"
  );
  const hasVaraDocUpdateRequest =
    shouldUpdatePassportDoc ||
    shouldUpdateProofOfFundsDoc ||
    shouldUpdateProofOfAddressDoc;
  const showVaraRejectionDetails =
    Boolean(varaRejectionReason) || normalizedVaraFilesToUpdate.length > 0;

  useEffect(() => {
    if (!hasNafathDocUpdateRequest) {
      setIsEditingNafathDocs(false);
      setIdProofFile(null);
      setAddressProofFile(null);
      setNafathDocErrors({});
    }
  }, [hasNafathDocUpdateRequest]);

  useEffect(() => {
    if (!hasVaraDocUpdateRequest) {
      setIsEditingVaraDocs(false);
      setPassportDocFile(null);
      setProofOfFundsDocFile(null);
      setProofOfAddressDocFile(null);
      setVaraDocErrors({});
    }
  }, [hasVaraDocUpdateRequest]);

  const onNafathFilePicked = (event, fileType) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      setNafathDocErrors((prev) => ({
        ...prev,
        [fileType]: "File size must not exceed 10 MB.",
      }));
      event.target.value = "";
      return;
    }
    if (fileType === "idProofDocument") {
      setIdProofFile(file);
    } else if (fileType === "addressProofDocument") {
      setAddressProofFile(file);
    }
    setNafathDocErrors((prev) => ({ ...prev, [fileType]: "", submit: "" }));
  };

  const handleSubmitNafathDocuments = async () => {
    const newErrors = {};
    if (shouldUpdateIdProof && !idProofFile) {
      newErrors.idProofDocument = "Please upload ID proof document.";
    }
    if (shouldUpdateAddressProof && !addressProofFile) {
      newErrors.addressProofDocument = "Please upload address proof document.";
    }
    if (!shouldUpdateIdProof && !shouldUpdateAddressProof && !idProofFile && !addressProofFile) {
      newErrors.submit = "No document requested for update by admin.";
    }

    setNafathDocErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const formData = new FormData();
    if (idProofFile) formData.append("idProofDocument", idProofFile);
    if (addressProofFile) formData.append("addressProofDocument", addressProofFile);

    try {
      await uploadNafathDocuments(formData).unwrap();
      await refetchProfile();
      setIsEditingNafathDocs(false);
      setIdProofFile(null);
      setAddressProofFile(null);
      setNafathDocErrors({});
    } catch (error) {
      setNafathDocErrors((prev) => ({
        ...prev,
        submit:
          error?.data?.message ||
          error?.data?.error ||
          error?.error ||
          "Could not upload Nafath documents.",
      }));
    }
  };

  const onVaraFilePicked = (event, fileType) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      setVaraDocErrors((prev) => ({
        ...prev,
        [fileType]: "File size must not exceed 10 MB.",
      }));
      event.target.value = "";
      return;
    }
    if (fileType === "passportDoc") setPassportDocFile(file);
    else if (fileType === "proofOfFundsDoc") setProofOfFundsDocFile(file);
    else if (fileType === "proofOfAddressDoc") setProofOfAddressDocFile(file);
    setVaraDocErrors((prev) => ({ ...prev, [fileType]: "", submit: "" }));
  };

  const handleSubmitVaraVerificationDocument = async () => {
    const newErrors = {};
    if (!String(varaPassportNumber ?? "").trim()) {
      newErrors.passportNumber = "Passport number is required.";
    }
    if (!String(varaCryptoWalletAddress ?? "").trim()) {
      newErrors.cryptoWalletAddress = "Crypto wallet address is required.";
    }
    if (!String(varaSourceOfFunds ?? "").trim()) {
      newErrors.sourceOfFunds = "Source of funds is required.";
    }
    if (!String(varaTradingExperience ?? "").trim()) {
      newErrors.tradingExperience = "Trading experience is required.";
    }
    if (shouldUpdatePassportDoc && !passportDocFile) {
      newErrors.passportDoc = "Please upload passport document.";
    }
    if (shouldUpdateProofOfFundsDoc && !proofOfFundsDocFile) {
      newErrors.proofOfFundsDoc = "Please upload proof of funds document.";
    }
    if (shouldUpdateProofOfAddressDoc && !proofOfAddressDocFile) {
      newErrors.proofOfAddressDoc = "Please upload proof of address document.";
    }
    if (
      !shouldUpdatePassportDoc &&
      !shouldUpdateProofOfFundsDoc &&
      !shouldUpdateProofOfAddressDoc &&
      !passportDocFile &&
      !proofOfFundsDocFile &&
      !proofOfAddressDocFile
    ) {
      newErrors.submit = "No document requested for update by admin.";
    }

    setVaraDocErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const formData = new FormData();
    if (passportDocFile) formData.append("passportDoc", passportDocFile);
    if (proofOfFundsDocFile) formData.append("proofOfFundsDoc", proofOfFundsDocFile);
    if (proofOfAddressDocFile) formData.append("proofOfAddressDoc", proofOfAddressDocFile);
    formData.append("passportNumber", String(varaPassportNumber ?? "").trim());
    formData.append("cryptoWalletAddress", String(varaCryptoWalletAddress ?? "").trim());
    formData.append("sourceOfFunds", String(varaSourceOfFunds ?? "").trim());
    formData.append("tradingExperience", String(varaTradingExperience ?? "").trim());

    try {
      await updateVaraVerificationDocument(formData).unwrap();
      await refetchProfile();
      setIsEditingVaraDocs(false);
      setPassportDocFile(null);
      setProofOfFundsDocFile(null);
      setProofOfAddressDocFile(null);
      setVaraDocErrors({});
    } catch (error) {
      setVaraDocErrors((prev) => ({
        ...prev,
        submit:
          error?.data?.message ||
          error?.data?.error ||
          error?.error ||
          "Could not update VARA verification.",
      }));
    }
  };

  if (isLoading) {
    return <SettingsSkeleton />;
  }

  if (isError || !profile) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography color="error">
          Unable to load profile. Please try again later.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        px: 2,
        display: "flex",
      }}
    >
      <Box sx={{ width: "100%", minWidth: 0 }}>
        {/* Back link */}
        <Button
          onClick={() => navigate("/asset/dashboard")}
          startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
          sx={{
            textTransform: "none",
            color: "text.primary",
            mb: 2,
            px: 0,
            "&:hover": { backgroundColor: "transparent" },
          }}
        >
          Back to Dashboard
        </Button>

        {/* Page title */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: 1,
              bgcolor: "background.headerIcon",
              border: (theme) => `1px solid ${theme.palette.border.light}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SettingsOutlinedIcon sx={{ color: "text.slate" }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ color: "text.primary" }}>
              Account &amp; Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your security and preferences
            </Typography>
          </Box>
        </Stack>

        {/* PROFILE INFORMATION */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            p: 3,
            mb: 3,
            backgroundColor: "background.paper",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
            Profile Information
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your verified identity and contact details
          </Typography>

          <Grid container spacing={3}>
            {/* Profile left */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction="row" spacing={2} alignItems="center" mb={2.5}>
                <Avatar
                  sx={{ width: 64, height: 64 }}
                // you can later plug profile avatar url if available
                >
                  {fullName ? fullName.charAt(0).toUpperCase() : "U"}
                </Avatar>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle1" fontWeight={600}>
                      {fullName || "User"}
                    </Typography>
                    <Chip
                      size="small"
                      icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                      label={isKycVerified ? "Verified" : "KYC Pending"}
                      sx={{
                        backgroundColor: (theme) =>
                          isKycVerified
                            ? theme.palette.accent.success.bg
                            : theme.palette.accent.warning.pendingBg,
                        color: (theme) =>
                          isKycVerified
                            ? theme.palette.accent.success.text
                            : theme.palette.accent.warning.pendingText,
                        "& .MuiChip-icon": {
                          color: (theme) =>
                            isKycVerified
                              ? theme.palette.accent.success.text
                              : theme.palette.accent.warning.pendingText,
                        },
                      }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {roleLabel}
                    {createdAtLabel ? ` since ${createdAtLabel}` : ""}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${roleLabel} (${kycStatusText})`}
                    sx={{
                      mt: 1,
                      backgroundColor: (theme) => theme.palette.accent.warning.cardBg,
                      color: (theme) => theme.palette.accent.warning.cardText,
                    }}
                  />
                </Box>
              </Stack>

              {/* Email / Phone */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: "uppercase" }}
                  >
                    Email Address
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    mt={0.5}
                  >
                    <EmailIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body2">
                      {profile.email || "-"}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: "uppercase" }}
                  >
                    Phone Number
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    mt={0.5}
                  >
                    <PhoneIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body2">
                      {profile.phoneNumber || "-"}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Grid>

            {/* Identity / Property cards */}
            <Grid size={{ xs: 12, md: 6 }}>
              {/* National ID card (from API) */}
              {/* <Paper
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                  backgroundColor: (theme) => theme.palette.accent.success.soft,
                  borderColor: (theme) => theme.palette.accent.success.border,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Verified ID Type
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  National ID
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {profile.nationalId ? `ID: ${profile.nationalId}` : "Not set"}
                </Typography>
              </Paper> */}

              <Grid container spacing={2}>
                {/* Identity Verification / Nafath */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      p: 2,
                      height: "100%",
                      backgroundColor: (theme) => theme.palette.accent.success.softer,
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ mb: 0.5 }}
                    >
                      Identity Verification
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Nafath Authentication
                    </Typography>
                    <Chip
                      size="small"
                      label={
                        profile.verifiedByNafath
                          ? "Verified via Nafath"
                          : `Status: ${nafathStatusText}`
                      }
                      sx={{
                        mt: 1.5,
                        backgroundColor: (theme) =>
                          profile.verifiedByNafath
                            ? theme.palette.accent.success.bg
                            : theme.palette.accent.warning.pendingBg,
                        color: (theme) =>
                          profile.verifiedByNafath
                            ? theme.palette.accent.success.text
                            : theme.palette.accent.warning.pendingText,
                      }}
                    />
                    {profile.nafathVerifiedAt && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        Verified on{" "}
                        {new Date(profile.nafathVerifiedAt).toLocaleDateString(
                          "en-GB"
                        )}
                      </Typography>
                    )}
                    {showNafathRejectionDetails && (
                      <Box sx={{ mt: 1.5 }}>
                        {nafathRejectionReason && (
                          <Typography
                            variant="caption"
                            sx={{ display: "block", color: "error.main", mb: 0.75 }}
                          >
                            Rejection reason: {nafathRejectionReason}
                          </Typography>
                        )}
                        {nafathFilesNeedToBeUpdatedRaw.length > 0 && (
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {nafathFilesNeedToBeUpdatedRaw.map((fileKey) => (
                              <Chip
                                key={`${fileKey}`}
                                size="small"
                                label={nafathFileLabel(fileKey)}
                                sx={{
                                  backgroundColor: (theme) =>
                                    theme.palette.accent.warning.pendingBg,
                                  color: (theme) => theme.palette.accent.warning.pendingText,
                                }}
                              />
                            ))}
                          </Stack>
                        )}
                        {hasNafathDocUpdateRequest && (
                          <Button
                            variant="text"
                            size="small"
                            startIcon={<EditOutlinedIcon />}
                            onClick={() => setIsEditingNafathDocs((prev) => !prev)}
                            sx={{ textTransform: "none", mt: 0.5, px: 0 }}
                          >
                            {isEditingNafathDocs ? "Cancel edit" : "Edit rejected documents"}
                          </Button>
                        )}
                      </Box>
                    )}
                  </Paper>
                </Grid>

                {/* KYC Status */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      p: 2,
                      height: "100%",
                      backgroundColor: (theme) => theme.palette.accent.warning.cardBg,
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ mb: 0.5 }}
                    >
                      KYC Status
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Overall KYC review
                    </Typography>
                    <Chip
                      size="small"
                      label={kycStatusText}
                      sx={{
                        mt: 1.5,
                        backgroundColor: (theme) =>
                          isKycVerified
                            ? theme.palette.accent.success.bg
                            : theme.palette.accent.warning.pendingBg,
                        color: (theme) =>
                          isKycVerified
                            ? theme.palette.accent.success.text
                            : theme.palette.accent.warning.pendingText,
                      }}
                    />
                    {profile.kycVerifiedAt && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        Verified on{" "}
                        {new Date(profile.kycVerifiedAt).toLocaleDateString(
                          "en-GB"
                        )}
                      </Typography>
                    )}
                  </Paper>
                </Grid>

                {/* VARA Verification */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      p: 2,
                      height: "100%",
                      backgroundColor: (theme) =>
                        isVaraApproved
                          ? theme.palette.accent.success.softer
                          : theme.palette.accent.warning.cardBg,
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ mb: 0.5 }}
                    >
                      VARA Verification
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Virtual Assets Regulatory Authority
                    </Typography>
                    <Chip
                      size="small"
                      label={varaStatusText}
                      sx={{
                        mt: 1.5,
                        backgroundColor: (theme) =>
                          isVaraApproved
                            ? theme.palette.accent.success.bg
                            : theme.palette.accent.warning.pendingBg,
                        color: (theme) =>
                          isVaraApproved
                            ? theme.palette.accent.success.text
                            : theme.palette.accent.warning.pendingText,
                      }}
                    />
                    {varaDetails?.varaVerifiedAt && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        Verified on{" "}
                        {new Date(varaDetails.varaVerifiedAt).toLocaleDateString("en-GB")}
                      </Typography>
                    )}
                    {showVaraRejectionDetails && (
                      <Box sx={{ mt: 1.5 }}>
                        {varaRejectionReason && (
                          <Typography
                            variant="caption"
                            sx={{ display: "block", color: "error.main", mb: 0.75 }}
                          >
                            Rejection reason: {varaRejectionReason}
                          </Typography>
                        )}
                        {varaFilesNeedToBeUpdatedRaw.length > 0 && (
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {varaFilesNeedToBeUpdatedRaw.map((fileKey) => (
                              <Chip
                                key={`vara-${fileKey}`}
                                size="small"
                                label={varaFileLabel(fileKey)}
                                sx={{
                                  backgroundColor: (theme) =>
                                    theme.palette.accent.warning.pendingBg,
                                  color: (theme) => theme.palette.accent.warning.pendingText,
                                }}
                              />
                            ))}
                          </Stack>
                        )}
                        {hasVaraDocUpdateRequest && (
                          <Button
                            variant="text"
                            size="small"
                            startIcon={<EditOutlinedIcon />}
                            onClick={() => {
                              setIsEditingVaraDocs((prev) => {
                                const next = !prev;
                                if (next) {
                                  const vd = profile?.varaDetails;
                                  if (vd && typeof vd === "object") {
                                    setVaraPassportNumber(
                                      String(vd.varaPassportNumber ?? "").trim()
                                    );
                                    setVaraCryptoWalletAddress(
                                      String(vd.varaCryptoWalletAddress ?? "").trim()
                                    );
                                    setVaraSourceOfFunds(
                                      String(vd.varaSourceOfFunds ?? "").trim()
                                    );
                                    setVaraTradingExperience(
                                      String(vd.varaTradingExperience ?? "").trim()
                                    );
                                  }
                                }
                                return next;
                              });
                            }}
                            sx={{ textTransform: "none", mt: 0.5, px: 0 }}
                          >
                            {isEditingVaraDocs ? "Cancel edit" : "Edit VARA documents"}
                          </Button>
                        )}
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {isEditingNafathDocs && hasNafathDocUpdateRequest && (
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                mt: 3,
                borderRadius: 2,
                p: 2,
                borderColor: (theme) => theme.palette.border.light,
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Update Nafath Documents
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                Upload only the documents requested by admin.
              </Typography>

              <Stack spacing={2}>
                {shouldUpdateIdProof && (
                  <Box>
                    <input
                      ref={idProofInputRef}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      hidden
                      onChange={(e) => onNafathFilePicked(e, "idProofDocument")}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadOutlinedIcon />}
                      onClick={() => idProofInputRef.current?.click()}
                      sx={{ textTransform: "none" }}
                    >
                      Upload ID Proof
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      {idProofFile ? idProofFile.name : "No file selected"}
                    </Typography>
                    {nafathDocErrors.idProofDocument && (
                      <Typography variant="caption" color="error.main" sx={{ display: "block", mt: 0.5 }}>
                        {nafathDocErrors.idProofDocument}
                      </Typography>
                    )}
                  </Box>
                )}

                {shouldUpdateAddressProof && (
                  <Box>
                    <input
                      ref={addressProofInputRef}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      hidden
                      onChange={(e) => onNafathFilePicked(e, "addressProofDocument")}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadOutlinedIcon />}
                      onClick={() => addressProofInputRef.current?.click()}
                      sx={{ textTransform: "none" }}
                    >
                      Upload Address Proof
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      {addressProofFile ? addressProofFile.name : "No file selected"}
                    </Typography>
                    {nafathDocErrors.addressProofDocument && (
                      <Typography variant="caption" color="error.main" sx={{ display: "block", mt: 0.5 }}>
                        {nafathDocErrors.addressProofDocument}
                      </Typography>
                    )}
                  </Box>
                )}

                {nafathDocErrors.submit && (
                  <Typography variant="caption" color="error.main">
                    {nafathDocErrors.submit}
                  </Typography>
                )}

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={handleSubmitNafathDocuments}
                    disabled={isUploadingNafathDocuments}
                    sx={{ textTransform: "none" }}
                  >
                    {isUploadingNafathDocuments ? "Uploading..." : "Submit Documents"}
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => setIsEditingNafathDocs(false)}
                    disabled={isUploadingNafathDocuments}
                    sx={{ textTransform: "none" }}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          )}

          {isEditingVaraDocs && hasVaraDocUpdateRequest && (
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                mt: 3,
                borderRadius: 2,
                p: 2,
                borderColor: (theme) => theme.palette.border.light,
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Update VARA verification
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                Upload requested documents and confirm your VARA details. Values are loaded from your
                profile.
              </Typography>

              <Stack spacing={2}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Passport number"
                      fullWidth
                      size="small"
                      value={varaPassportNumber}
                      onChange={(e) => {
                        setVaraPassportNumber(e.target.value);
                        setVaraDocErrors((prev) => ({ ...prev, passportNumber: "" }));
                      }}
                      error={Boolean(varaDocErrors.passportNumber)}
                      helperText={varaDocErrors.passportNumber || undefined}
                      disabled={isUploadingVaraDocuments}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Crypto wallet address"
                      fullWidth
                      size="small"
                      value={varaCryptoWalletAddress}
                      onChange={(e) => {
                        setVaraCryptoWalletAddress(e.target.value);
                        setVaraDocErrors((prev) => ({ ...prev, cryptoWalletAddress: "" }));
                      }}
                      error={Boolean(varaDocErrors.cryptoWalletAddress)}
                      helperText={varaDocErrors.cryptoWalletAddress || undefined}
                      disabled={isUploadingVaraDocuments}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Source of funds"
                      fullWidth
                      size="small"
                      value={varaSourceOfFunds}
                      onChange={(e) => {
                        setVaraSourceOfFunds(e.target.value);
                        setVaraDocErrors((prev) => ({ ...prev, sourceOfFunds: "" }));
                      }}
                      error={Boolean(varaDocErrors.sourceOfFunds)}
                      helperText={varaDocErrors.sourceOfFunds || undefined}
                      disabled={isUploadingVaraDocuments}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Trading experience"
                      fullWidth
                      size="small"
                      value={varaTradingExperience}
                      onChange={(e) => {
                        setVaraTradingExperience(e.target.value);
                        setVaraDocErrors((prev) => ({ ...prev, tradingExperience: "" }));
                      }}
                      error={Boolean(varaDocErrors.tradingExperience)}
                      helperText={varaDocErrors.tradingExperience || undefined}
                      disabled={isUploadingVaraDocuments}
                    />
                  </Grid>
                </Grid>

                {shouldUpdatePassportDoc && (
                  <Box>
                    <input
                      ref={passportDocInputRef}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      hidden
                      onChange={(e) => onVaraFilePicked(e, "passportDoc")}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadOutlinedIcon />}
                      onClick={() => passportDocInputRef.current?.click()}
                      sx={{ textTransform: "none" }}
                      disabled={isUploadingVaraDocuments}
                    >
                      Upload passport
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      {passportDocFile ? passportDocFile.name : "No file selected"}
                    </Typography>
                    {varaDocErrors.passportDoc && (
                      <Typography variant="caption" color="error.main" sx={{ display: "block", mt: 0.5 }}>
                        {varaDocErrors.passportDoc}
                      </Typography>
                    )}
                  </Box>
                )}

                {shouldUpdateProofOfFundsDoc && (
                  <Box>
                    <input
                      ref={proofOfFundsDocInputRef}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      hidden
                      onChange={(e) => onVaraFilePicked(e, "proofOfFundsDoc")}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadOutlinedIcon />}
                      onClick={() => proofOfFundsDocInputRef.current?.click()}
                      sx={{ textTransform: "none" }}
                      disabled={isUploadingVaraDocuments}
                    >
                      Upload proof of funds
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      {proofOfFundsDocFile ? proofOfFundsDocFile.name : "No file selected"}
                    </Typography>
                    {varaDocErrors.proofOfFundsDoc && (
                      <Typography variant="caption" color="error.main" sx={{ display: "block", mt: 0.5 }}>
                        {varaDocErrors.proofOfFundsDoc}
                      </Typography>
                    )}
                  </Box>
                )}

                {shouldUpdateProofOfAddressDoc && (
                  <Box>
                    <input
                      ref={proofOfAddressDocInputRef}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      hidden
                      onChange={(e) => onVaraFilePicked(e, "proofOfAddressDoc")}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadOutlinedIcon />}
                      onClick={() => proofOfAddressDocInputRef.current?.click()}
                      sx={{ textTransform: "none" }}
                      disabled={isUploadingVaraDocuments}
                    >
                      Upload proof of address
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      {proofOfAddressDocFile ? proofOfAddressDocFile.name : "No file selected"}
                    </Typography>
                    {varaDocErrors.proofOfAddressDoc && (
                      <Typography variant="caption" color="error.main" sx={{ display: "block", mt: 0.5 }}>
                        {varaDocErrors.proofOfAddressDoc}
                      </Typography>
                    )}
                  </Box>
                )}

                {varaDocErrors.submit && (
                  <Typography variant="caption" color="error.main">
                    {varaDocErrors.submit}
                  </Typography>
                )}

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={handleSubmitVaraVerificationDocument}
                    disabled={isUploadingVaraDocuments}
                    sx={{ textTransform: "none" }}
                  >
                    {isUploadingVaraDocuments ? "Submitting..." : "Submit VARA update"}
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => setIsEditingVaraDocs(false)}
                    disabled={isUploadingVaraDocuments}
                    sx={{ textTransform: "none" }}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          )}

          {/* KYC status banner */}
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              borderRadius: 2,
              p: 2,
              background: (theme) =>
                isKycVerified
                  ? theme.palette.brand.cyanSoft
                  : theme.palette.accent.warning.cardBg,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              KYC Status:
            </Typography>{" "}
            <Typography variant="caption">
              {isKycVerified
                ? "Your identity has been verified and approved. All compliance requirements are met."
                : "Your KYC is currently pending. Please complete any remaining steps in your profile to get fully verified."}
            </Typography>
          </Paper>
        </Paper>

        {/* MY WALLET — same flow as investor settings; separate `owner_wallet_*` storage */}
        <Paper
          elevation={0}
          sx={{ borderRadius: 3, p: 3, mb: 3, backgroundColor: "background.paper" }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            flexWrap="wrap"
            gap={2}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                My Wallet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your blockchain wallet for listings and token management
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<AccountBalanceWalletOutlined />}
              onClick={() => setConnectOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                bgcolor: "#0d9488",
                "&:hover": { bgcolor: "#0f766e" },
              }}
            >
              {walletAddress ? "Change Wallet" : "Connect Wallet"}
            </Button>
          </Stack>

          {!walletAddress ? (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 3,
                p: 4,
                textAlign: "center",
                borderColor: "#e5e7eb",
                mb: 3,
              }}
            >
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  bgcolor: "#e0f2fe",
                  display: "grid",
                  placeItems: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <AccountBalanceWalletOutlined sx={{ fontSize: 36, color: "#0284c7" }} />
              </Box>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
                No Wallet Connected
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3, maxWidth: 420, mx: "auto" }}
              >
                Connect your crypto wallet to manage tokenized assets, receive proceeds, and
                verify on-chain actions.
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<AccountBalanceWalletOutlined />}
                onClick={() => setConnectOpen(true)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  px: 4,
                  py: 1.25,
                  borderRadius: 2,
                  bgcolor: "#0284c7",
                  "&:hover": { bgcolor: "#0369a1" },
                }}
              >
                Connect Wallet
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 2 }}
              >
                Supported wallets: MetaMask, WalletConnect, and Coinbase Wallet
              </Typography>
            </Paper>
          ) : (
            <>
              <Paper
                sx={{
                  borderRadius: 3,
                  p: 3,
                  background:
                    "linear-gradient(90deg,#06124c 0%,#071b5f 40%,#060e3d 100%)",
                  color: "#ffffff",
                  mb: 2,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  flexWrap="wrap"
                  gap={1}
                >
                  <Typography variant="caption" sx={{ opacity: 0.85 }}>
                    Wallet Address
                  </Typography>
                  <Stack direction="row" spacing={0.75}>
                    {apiWalletVerified && (
                      <Chip
                        label="Verified"
                        size="small"
                        sx={{ backgroundColor: "#22c55e", color: "#fff", fontWeight: 700 }}
                      />
                    )}
                    <Chip
                      label={walletProvider ? `${walletProvider} · Connected` : "Connected"}
                      size="small"
                      sx={{
                        backgroundColor: "rgba(255,255,255,0.18)",
                        color: "#fff",
                        fontWeight: 700,
                      }}
                    />
                  </Stack>
                </Stack>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                    mt: 1,
                    fontWeight: 600,
                  }}
                >
                  {walletAddress}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ mt: 1.5, display: "block", opacity: 0.85 }}
                >
                  {networkLabel(WALLET_CHAIN_ID)} (Chain ID: {WALLET_CHAIN_ID})
                </Typography>
                {apiWalletVerifiedAt && (
                  <Typography variant="caption" sx={{ mt: 0.5, display: "block", opacity: 0.7 }}>
                    Verified on {formatProfileDate(apiWalletVerifiedAt)}
                  </Typography>
                )}
                <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mt: 2 }}>
                  <Button
                    component="a"
                    href={blockExplorerAddressUrl(walletAddress)}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    size="small"
                    endIcon={<OpenInNewIcon />}
                    sx={{
                      color: "#ffffff",
                      borderColor: "rgba(255,255,255,0.45)",
                      textTransform: "none",
                      "&:hover": { borderColor: "#ffffff" },
                    }}
                  >
                    View on Explorer
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleDisconnectWallet}
                    sx={{
                      color: "#fff",
                      textTransform: "none",
                      fontWeight: 600,
                      background: "#e91212",
                      "&:hover": { background: "#c0392b" },
                    }}
                  >
                    Disconnect
                  </Button>
                </Stack>
              </Paper>
{/* 
              <Grid container spacing={2} sx={{ mb: 1 }}>
                {[
                  {
                    label: "Listed Assets Value",
                    sub: "Across your published listings",
                    value: "SAR —",
                    bg: "#ecfdf5",
                    accent: "#047857",
                  },
                  {
                    label: "Proceeds (MTD)",
                    sub: "Token sales this month",
                    value: "SAR —",
                    bg: "#e0f2fe",
                    accent: "#0369a1",
                  },
                  {
                    label: "Pending Settlement",
                    sub: "Awaiting payout",
                    value: "SAR —",
                    bg: "#f3e8ff",
                    accent: "#7c3aed",
                  },
                ].map((card) => (
                  <Grid size={{ xs: 12, md: 4 }} key={card.label} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        bgcolor: card.bg,
                        border: "1px solid rgba(0,0,0,0.04)",
                        flex: 1,
                        width: "100%",
                        minHeight: 120,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ color: card.accent }}
                      >
                        {card.label}
                      </Typography>
                      <Typography variant="h6" fontWeight={800} sx={{ mt: 1, color: "#0f172a" }}>
                        {card.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: "auto" }}>
                        {card.sub}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid> */}
            </>
          )}

          <InvestorConnectWalletDialog
            open={connectOpen}
            onClose={() => setConnectOpen(false)}
            onLinked={handleWalletLinked}
            storageKeys={OWNER_WALLET_STORAGE_KEYS}
          />
        </Paper>

        {/* STRIPE ACCOUNT */}
        <Paper
          elevation={0}
          sx={{ borderRadius: 3, p: 3, mb: 3, backgroundColor: "background.paper" }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            flexWrap="wrap"
            gap={2}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Stripe Account
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Connect your Stripe account to receive payouts from token sales
              </Typography>
            </Box>
          </Stack>

          {(() => {
            const isFullyConnected =
              stripeStatus?.connected &&
              stripeStatus?.chargesEnabled &&
              stripeStatus?.payoutsEnabled &&
              stripeStatus?.detailsSubmitted;

            let buttonText;
            if (!stripeStatus?.connected) {
              buttonText = "Connect Stripe";
            } else if (
              !stripeStatus?.chargesEnabled ||
              !stripeStatus?.payoutsEnabled ||
              !stripeStatus?.detailsSubmitted
            ) {
              buttonText = "Complete Stripe Setup";
            } else {
              buttonText = "Stripe Connected";
            }

            return (
              <Stack spacing={2} alignItems="flex-start">
                {profile?.stripeAccountId && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textTransform: "uppercase", display: "block" }}
                    >
                      Stripe Account ID
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{ fontFamily: "monospace", wordBreak: "break-all", mt: 0.5 }}
                    >
                      {profile.stripeAccountId}
                    </Typography>
                  </Box>
                )}

                {isLoadingStripeStatus ? (
                  <Typography variant="caption" color="text.secondary">
                    Loading Stripe status...
                  </Typography>
                ) : stripeStatus ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {[
                      { label: "Charges Enabled", value: stripeStatus.chargesEnabled },
                      { label: "Payouts Enabled", value: stripeStatus.payoutsEnabled },
                      { label: "Details Submitted", value: stripeStatus.detailsSubmitted },
                    ].map((row) => (
                      <Chip
                        key={row.label}
                        size="small"
                        label={`${row.label}: ${row.value ? "Yes" : "No"}`}
                        sx={{
                          backgroundColor: (theme) =>
                            row.value
                              ? theme.palette.accent.success.bg
                              : theme.palette.accent.warning.pendingBg,
                          color: (theme) =>
                            row.value
                              ? theme.palette.accent.success.text
                              : theme.palette.accent.warning.pendingText,
                          fontWeight: 600,
                        }}
                      />
                    ))}
                  </Stack>
                ) : null}

                <Button
                  variant="contained"
                  size="medium"
                  onClick={handleConnectStripeAccount}
                  disabled={isConnectingStripe || isFullyConnected}
                  startIcon={isFullyConnected ? <CheckCircleIcon /> : undefined}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                    bgcolor: isFullyConnected ? "#16a34a" : "#635bff",
                    "&:hover": { bgcolor: isFullyConnected ? "#15803d" : "#4f46e5" },
                    "&.Mui-disabled": isFullyConnected
                      ? { bgcolor: "#16a34a", color: "#fff", opacity: 0.9 }
                      : undefined,
                  }}
                >
                  {isConnectingStripe ? "Opening..." : buttonText}
                </Button>

                {stripeConnectError && (
                  <Typography variant="caption" color="error.main">
                    {stripeConnectError}
                  </Typography>
                )}
              </Stack>
            );
          })()}
        </Paper>
      </Box>
    </Box>
  );
}

export default Settings;
